import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, useTheme } from "@mui/material";
import { FormPropertyProps } from "../../FormProperty";
import { formatUnitAbbreviation } from "src/utils/formatting";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { SSQTextInput } from "src/components/SSQ/FormFields/SSQTextInput";
import { manageTooltipText } from "src/utils/tooltips";
import { sanitizeRichTextHtml } from "src/components/HTMLBox/sanitizeRichTextHtml";
import { getDisplayValueForScore } from "src/utils/scores";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { usePropertyContext } from "../../context/usePropertyContext";
import { FieldValueLayout } from "../FieldValueLayout";

export const Text = (props: FormPropertyProps) => {
    const { t } = useTranslation();
    const { selectedScorePrecision } = useEntityScores();
    const theme = useTheme();
    const { property, position, showSubscore, dimensionValue } = props;

    const { post, onUpdate, scoreUnit, getValidationError, canEdit, getElement } = usePropertyContext();

    const element = getElement(property.id, position);

    const _defaultValue = property.defaultValue ?? "";
    const initialValue = element?.value ?? "";

    const [previousValue, setPreviousValue] = useState(initialValue);
    const [value, setValue] = useState(initialValue);
    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);

    const genericValue = property.genericValue;

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    if (!post) return <CircularProgress />;

    const unit = formatUnitAbbreviation(property.unit);

    const updatePropertyValue = (value?: string, isGenericValue?: boolean) => {
        const sanitizedValue = value?.trim() ?? "";

        onUpdate({
            postId: post.id,
            propertyId: Number(property.id),
            value: sanitizedValue,
            position: Number(props.position ?? 0),
            isGenericValue: isGenericValue,
        });
    };

    const defaultValueText = property.readonly ? t("cette_valeur_est_en_lecture_seule") : t("valeur_par_defaut");

    const tooltipText = manageTooltipText((coalescedText: string) => {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(coalescedText) }} />;
    }, property);
    const isDefaultActivated = !property.readonly && _defaultValue && !genericValueSelected;
    const defaultValue = isDefaultActivated ? _defaultValue : undefined;
    const propertyScoreValue = OlapScoresService.normalizeNumericalScore(dimensionValue?.score.value);

    const shouldDisplayScore = propertyScoreValue !== null && showSubscore === true;

    const scoreProperty = {
        score:
            shouldDisplayScore !== false ? getDisplayValueForScore(propertyScoreValue, selectedScorePrecision) : null,
        unit: scoreUnit,
    };

    const onCalculate = (newValue: string) => {
        if (newValue === previousValue) return;
        updatePropertyValue(newValue, undefined);
    };

    const validationState = getValidationError(property.id, position ?? 0);

    const isWarningState = validationState.validationSeverity === "warning";
    const isErrorState = validationState.hasValidationMessages && validationState.validationSeverity !== "warning";
    const warningInputSx =
        isWarningState ?
            {
                "&.MuiInputBase-root": {
                    border: `1px solid ${theme.custom.orange[600]}`,
                    backgroundColor: theme.custom.orange[50],
                    color: theme.custom.orange[700],
                    ":hover": {
                        backgroundColor: theme.custom.orange[100],
                    },
                },
            }
        :   undefined;

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                defaultValue: defaultValue ?? null,
                genericValue: genericValue ?? null,
                isGenericValueSelected: genericValueSelected,
                dimensionValue: dimensionValue,
                forceHideScore: showSubscore === false,
                setDefaultValue: () => {
                    if (defaultValue) {
                        onCalculate?.(defaultValue);
                    }
                },
                setGenericValue: () => {
                    const v = !genericValueSelected ? genericValue : "";
                    setGenericValueSelected((prevState) => !prevState);
                    setValue(v ?? "");
                    updatePropertyValue(v, !genericValueSelected);
                },
            }}
        >
            <SSQTextInput
                error={isErrorState}
                onCalculate={onCalculate}
                // score={scoreProperty}
                // tooltipText={tooltipText}
                // defaultOption={
                //     defaultValue === undefined ? undefined : (
                //         {
                //             value: defaultValue,
                //             label: defaultValueText,
                //         }
                //     )
                // }
                value={value !== "_empty_" ? value : ""}
                onChange={(newValue) => {
                    setValue(newValue);
                }}
                // unit={unit}
                disabled={property.readonly || !canEdit || genericValueSelected}
                ExtraInputProps={{
                    onFocus: (e) => {
                        const isFocusDirect = !e.relatedTarget;

                        if (!isFocusDirect) {
                            return;
                        }

                        setPreviousValue(value);
                    },
                    sx: {
                        ...warningInputSx,
                        width: "100%",
                    },
                }}
            />
        </FieldValueLayout>
    );
};
